<template>
  <div v-bind:id="id" class="input-text-template-selector">
    <transition-group class="template-container" tag="div" name="slide-pills">
      <!-- テンプレート(pill) -->
      <button
        v-for="template in templatesForPills"
        v-bind:key="template.id"
        v-ifield-b-tooltip.hover="{
          disabled: template.isNoEllipsis,
          title: template.text,
        }"
        v-bind:id="`${id}-pill-${template.id}`"
        class="template-pill"
        v-on:click="$_onClickTemplate(template)"
      >
        {{ template.displayText }}
      </button>
      <!-- リストボタン -->
      <button
        v-if="!!templatesForList.length"
        v-ifield-b-tooltip.hover
        v-bind:id="elementId.listButton"
        v-bind:key="'button'"
        variant="success"
        v-bind:title="$t('appInputTextTemplateSelector.action.showHiddenList')"
        class="template-pill list-button"
        v-on:click="$_onClickListButton"
      >
        <icon-double-arrow-right class="icon-others" />
      </button>
    </transition-group>
    <!-- リスト -->
    <b-popover v-if="!!templatesForList.length" v-bind="popoverProps">
      <transition-group class="template-list" tag="ul" name="slide-items">
        <li
          v-for="template in templatesForList"
          v-bind:key="template.id"
          v-bind:id="`${id}-item-${template.id}`"
          class="template-list-item"
          v-on:click="$_onClickTemplate(template)"
        >
          {{ template.text }}
        </li>
      </transition-group>
    </b-popover>
    <!-- 幅計測用隠し要素 -->
    <div v-if="toGetPillWidths" class="hidden-wrapper">
      <button
        v-for="template in templatesModified"
        v-bind:key="template.id"
        v-bind:id="$_getPillElementIdForCalc(template.id)"
        class="template-pill"
      >
        {{ template.displayText }}
      </button>
      <button
        v-bind:id="elementId.listButtonForCalc"
        class="template-pill list-button"
      >
        <icon-double-arrow-right class="icon-others" />
      </button>
    </div>
  </div>
</template>

<script>
import IconDoubleArrowRight from '@/assets/ifield/ic_double-arrow-right.svg?component'

export default {
  name: 'AppInputTextTemplateSelector',
  components: {
    'icon-double-arrow-right': IconDoubleArrowRight,
  },
  props: {
    /**
     * ルート要素のID
     * 各子要素IDのprefixとしても使用
     */
    id: {
      type: String,
      default: 'input-text-template-selector',
    },
    /**
     * テンプレートデータ
     */
    templates: {
      type: Array,
      default: () => [],
    },
    /**
     * 表示文字数
     */
    displayWordCount: {
      type: Number,
      default: 15,
    },
  },
  data: function () {
    return {
      // resizeObserver
      resizeObserver: null,
      // clientRect
      clientRect: {},
      // 幅取得用要素作成フラグ
      toGetPillWidths: false,
      // ピルの描画幅
      widths: [],
      // ボタンの幅(px)
      buttonWidth: 0,
      // 各行に表示できるピル数
      pillCountInRows: [],
    }
  },
  computed: {
    /**
     * 要素ID
     * @type {Object}
     */
    elementId: function () {
      return {
        listButton: `${this.id}-list-button`,
        listButtonForCalc: `${this.id}-list-button-for-calc`,
        listPopup: `${this.id}-list-popup`,
      }
    },
    /**
     * b-popoverのprops
     * @type {Object}
     */
    popoverProps: function () {
      return {
        id: this.elementId.listPopup,
        target: this.elementId.listButton,
        boundary: 'window',
        container: 'body',
        triggers: 'click blur',
        placement: 'right',
        customClass: 'template-list-popup',
        delay: 0,
        noFade: true,
      }
    },
    /**
     * 表示用に加工したテンプレートデータ
     * @param {Array.<Object>}
     */
    templatesModified: function () {
      return this.templates.map((item) => {
        const text = item.text_template
        const isNoEllipsis = text.length <= this.displayWordCount
        return {
          ...item,
          text,
          isNoEllipsis,
          displayText: isNoEllipsis
            ? text
            : text.slice(0, this.displayWordCount) + '...',
        }
      })
    },
    /**
     * 表示pill数
     * @type {Number}
     */
    pillCount: function () {
      return this.pillCountInRows.reduce((result, count) => {
        result += count
        return result
      }, 0)
    },
    /**
     * pillとして表示するテンプレートデータ
     * @param {Array.<Object>}
     */
    templatesForPills: function () {
      return this.templatesModified.slice(0, this.pillCount)
    },
    /**
     * 詳細リスト内表示するテンプレートデータ
     * @param {Array.<Object>}
     */
    templatesForList: function () {
      if (!this.pillCount) return []
      return this.templatesModified.slice(this.pillCount)
    },
  },
  watch: {
    templates: {
      handler: function () {
        this.$_refreshPillWidths()
      },
      immediate: true,
    },
    widths: function () {
      this.$_updatePillCountInRows()
    },
    clientRect: function () {
      this.$_updatePillCountInRows()
    },
  },
  mounted: function () {
    this.$nextTick(() => {
      this.$_updateClientRect()
      this.resizeObserver = new ResizeObserver(this.$_updateClientRect)
      this.resizeObserver.observe(this.$el)
    })
  },
  methods: {
    /**
     * 各pillボタンの幅の更新
     */
    $_refreshPillWidths: function () {
      this.toGetPillWidths = true
      this.$nextTick()
        .then(() => this.$nextTick())
        .then(() => {
          this.$_getPillWidths()
          this.$_getListButtonSize()
          this.toGetPillWidths = false
        })
    },

    /**
     * 各pillボタンの幅を取得する
     */
    $_getPillWidths: function () {
      this.widths = this.templates.map((item) => {
        const element = document.getElementById(
          this.$_getPillElementIdForCalc(item.id)
        )
        const rect = element.getBoundingClientRect()
        return { id: item.id, width: rect.width }
      })
    },

    /**
     * 計測用pillボタンのエレメントID取得
     * @param {Number} id テンプレートのID
     * @returns {String} エレメントID
     */
    $_getPillElementIdForCalc: function (id) {
      return `${this.id}-pill-for-calc-${id}`
    },

    /**
     * リスト表示ボタンのサイズを取得する
     */
    $_getListButtonSize: function () {
      const element = document.getElementById(this.elementId.listButtonForCalc)
      const rect = element.getBoundingClientRect()
      this.buttonWidth = rect.width
    },

    /**
     * clientRect更新
     */
    $_updateClientRect: function () {
      this.clientRect = this.$el.getBoundingClientRect()
    },

    /**
     * 各行に表示するpill数を更新する
     */
    $_updatePillCountInRows: function () {
      const counts = []
      const firstRowCount = this.$_calcDisplayPillCount()
      if (!firstRowCount) {
        this.pillCountInRows = counts
        return
      }
      counts.push(firstRowCount)

      // もう全て収まっていたら2行目は不要
      if (firstRowCount === this.templates.length) {
        this.pillCountInRows = counts
        return
      }

      const secondRowCount = this.$_calcDisplayPillCount(firstRowCount, true)
      counts.push(secondRowCount)
      this.pillCountInRows = counts
    },

    /**
     * 表示できるpillの数を計算する
     * @param {Number} [startIndex=0] 開始index
     * @param {Boolean} [padDetail=false] 詳細ボタンを考慮する場合はtrue
     */
    $_calcDisplayPillCount: function (startIndex = 0, padDetail = false) {
      let leftWidth = padDetail
        ? +this.clientRect.width - 8 - this.buttonWidth // 全体 - 詳細ボタン左gap - 詳細ボタン幅
        : +this.clientRect.width
      // そもそも幅が足りない
      if (leftWidth <= 0) return 0

      let count = 0
      const widths = this.widths.slice(startIndex)
      for (let i = 0; i < widths.length; ++i) {
        const width = widths[i].width
        leftWidth = leftWidth - width // 残り幅 - pill幅
        count += +(leftWidth >= 0) // 収まったのでカウント増
        if (leftWidth <= 0) {
          // もう収まらないので終了
          break
        }
        if (i !== widths.length - 1) {
          // 次に続くのでgap分を引く
          leftWidth -= 8
        }
      }

      // 詳細ボタンを考慮しない
      if (!padDetail) return count

      const itemCountForList = this.widths.length - startIndex - count
      // 全て収まった
      if (!itemCountForList) return count
      // 詳細ボタンが必要
      if (itemCountForList > 1) return count
      // 詳細ボタンが無かったら収まるか？
      leftWidth += 8 + this.buttonWidth
      if (leftWidth - this.widths[startIndex + count - 1].width >= 0) {
        count += 1
      }
      return count
    },

    /**
     * テンプレートアイテムクリック時処理
     * @param {Object} template 対象テンプレートデータ
     */
    $_onClickTemplate: function (template) {
      /**
       * テンプレート選択イベント
       * @param {Object} template 選択されたテンプレートデータ
       */
      this.$emit('selected', template)
    },

    /**
     * リスト表示ボタンクリック時処理
     */
    $_onClickListButton: function () {
      /**
       * テンプレートリスト表示イベント
       */
      this.$emit('list-show')
    },
  },
}
</script>

<style lang="scss" scoped>
$-pill-height: 2rem;
$-list-item-min-height: 2rem;

.template-container {
  width: 100%;
  max-height: calc(#{$-pill-height} * 2 + 8px);
  display: flex;
  justify-content: flex-start;
  align-items: flex-start;
  column-gap: 8px;
  row-gap: 8px;
  flex-wrap: wrap;
}

.template-pill {
  height: $-pill-height;
  font-size: $font-size-middle;
  padding: 4px 12px;
  background-color: #ffffff;
  border: 1px solid $button-border-color;
  border-radius: 1rem;
  color: $text-color-link;
  text-align: left;
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
  transition: background-color 0.2s;

  &:hover {
    cursor: pointer;
    background-color: $button-background-color;
  }
}

.list-button {
  display: flex;
  justify-content: center;
  align-items: center;
}

.icon-others {
  stroke: $text-color-link;
}

.template-list-popup {
  border: none;
  border-radius: 4px;
  box-shadow: $drop-shadow-middle;
  font-family: inherit;

  ::v-deep .arrow {
    display: none;
  }

  ::v-deep .popover-body {
    max-height: calc(#{$-pill-height} * 10);
    padding: 0;
    border: none;
    border-radius: 4px;
    overflow-y: auto;

    @include mixin-scrollbar();
    &::-webkit-scrollbar-track {
      border-top-right-radius: 4px;
      border-bottom-right-radius: 4px;
    }
  }
}

.template-list {
  margin: 0;
  padding: 0;
  background-color: #ffffff;
  border-radius: 4px;
}

.template-list-item {
  max-width: 18rem;
  min-height: $-pill-height;
  height: auto;
  font-size: $font-size-middle;
  color: $text-color-link;
  padding: 8px 16px;
  display: flex;
  justify-content: flex-start;
  align-items: center;
  flex-wrap: wrap;
  border-bottom: 1px solid $button-background-color;
  transition: background-color 0.2s;

  &:last-child {
    border-bottom: none;
  }

  &:hover {
    cursor: pointer;
    background-color: $button-background-color;
  }
}

.slide-pills-move,
.slide-items-move {
  transition: all 0.2s ease;
}

.slide-pills-enter-from,
.slide-pills-leave-to {
  opacity: 0;
}

.slide-items-enter-from,
.slide-items-leave-to {
  opacity: 0;
  height: 0;
}

.hidden-wrapper {
  z-index: -1;
  opacity: 0;
  pointer-events: none;
  position: absolute;
  display: flex;
  justify-content: flex-start;
  align-items: flex-start;
  flex-wrap: wrap;
}
</style>
